/**
 * SwitchWiringCanvas — Diseñador visual de interconexión de switches.
 *
 * Cada switch se dibuja como una tarjeta con su panel de puertos (estilo Visio):
 *   ┌──────────────┬────────────────────────────────┐
 *   │ SW-CORE-01   │  ▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪▪      │  ← los cables entran por aquí
 *   │ 24 puertos   │  1 2 3 4 5 … 24                │
 *   │ 8/24 conect. │                                │  ← y por aquí abajo
 *   └──────────────┴────────────────────────────────┘
 *
 * Los latiguillos NO van de puerto a puerto por encima de todo: cada cable sale
 * del borde superior o inferior de la tarjeta y el tramo exterior se pinta por
 * DEBAJO de las tarjetas. Dentro solo queda un latiguillo corto del puerto al
 * borde, así que ningún cable tapa el nombre ni la fila de puertos.
 *
 * - Arrastra una tarjeta para colocarla. La posición se guarda en el backend
 *   (RackElement.configuration.switch_canvas) y se comparte con el resto.
 * - Clic en un puerto libre → clic en otro puerto libre = latiguillo
 *   (PortConnection switch_port ↔ switch_port, el MISMO que ve la ficha del
 *   switch en la pestaña Puertos).
 * - Clic en un puerto ya ocupado = detalle del cable, editar o desconectar.
 *
 * Los cables hacia equipos que no son switches (paneles, APs, servidores…)
 * se marcan en el puerto pero NO se dibujan: este lienzo es switch↔switch.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import LocationFilter, { matchesLocation } from './LocationFilter';
import api from '../services/api';
import PortElementForm from './PortElementForm';
import {
  CABLE_COLORS,
  CABLE_TYPES,
  ORIGIN,
  PORT_H,
  PORT_W,
  VGAP,
  ZMAX,
  ZMIN,
  autoLayout,
  cableRoute,
  clamp,
  colorHex,
  exitSide,
  isFiber,
  polylineMid,
  portGrid,
  portLabel,
  portOffset,
  roundedPath,
  truncate,
} from './wiring/wiringGeometry';
import './SwitchWiringCanvas.css';

// La geometría del panel y el enrutado de cables viven en
// `wiring/wiringGeometry.js`: los comparte el Diseñador de Cableado.

/** Texto de la etiqueta de un cable según el modo: 'ports' | 'full' | 'notes'. */
function cableLabelLines(edge, mode) {
  if (!mode || mode === 'off') return [];
  const pa = portLabel(edge.a.port);
  const pb = portLabel(edge.b.port);
  const note = edge.conn?.notes ? truncate(edge.conn.notes, 42) : null;
  if (mode === 'notes') return note ? [note] : [];
  if (mode === 'ports') return [`${pa} ⇄ ${pb}`];
  const va = edge.a.port.vlan;
  const vb = edge.b.port.vlan;
  let vlan = null;
  if (va && vb) vlan = String(va) === String(vb) ? `VLAN ${va}` : `VLAN ${va} ⇄ ${vb}`;
  else if (va || vb) vlan = `VLAN ${va || vb}`;
  const head = `${truncate(edge.a.sw.name, 14)} ${pa} ⇄ ${truncate(edge.b.sw.name, 14)} ${pb}`;
  const meta = [edge.cable_type, vlan].filter(Boolean).join(' · ');
  return [head, meta, note].filter(Boolean);
}

/** Etiqueta de un enlace por panel/toma: lo interesante es qué hay en medio. */
function mirrorLabelLines(edge, mode) {
  if (!mode || mode === 'off') return [];
  const pa = portLabel(edge.a.port);
  const pb = portLabel(edge.b.port);
  const via = edge.viaText ? truncate(edge.viaText, 46) : null;
  // En modo corto lo útil no es «Port 24 ⇄ Port 24», sino por dónde pasa.
  if (mode === 'ports') return via ? [via] : [`${pa} ⇄ ${pb}`];
  if (mode === 'notes') return via ? [via] : [];
  const head = `${truncate(edge.a.sw.name, 12)} ${pa} ⇄ ${truncate(edge.b.sw.name, 12)} ${pb}`;
  return [head, via].filter(Boolean);
}

const LABEL_MODE_TEXT = {
  ports: 'Etiquetas',
  full: 'Etiquetas completas',
  notes: 'Solo notas',
  off: 'Sin etiquetas',
};

const INSTALL_LABEL = {
  planned: 'Planificado (diseño)',
  mounted: 'Montado',
  verified: 'Validado (producción)',
};

function SwitchWiringCanvas({ companyId, embedded = false }) {
  const [switches, setSwitches] = useState([]);
  const [positions, setPositions] = useState({});
  const [hiddenIds, setHiddenIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState('');

  const [selected, setSelected] = useState(null); // { switchId, portId }
  const [ghost, setGhost] = useState(null);       // { x, y } en coordenadas de lienzo
  const [hoverPortId, setHoverPortId] = useState(null);

  const [connectModal, setConnectModal] = useState(null); // { a, b }
  const [connModal, setConnModal] = useState(null);       // { conn, ... }
  const [form, setForm] = useState({
    cable_type: 'Cat6', cable_color: 'azul', cable_length_m: '', notes: '',
  });

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });

  // Etiquetas de los cables: 'ports' (P1 ⇄ P5), 'full' (switch + VLAN + nota)
  // u 'off'. Se recuerda entre sesiones.
  const [labelMode, setLabelMode] = useState(() => {
    try { return localStorage.getItem('cmdb.swd.labels') || 'ports'; } catch { return 'ports'; }
  });
  const [isFull, setIsFull] = useState(false);
  const [showNewSwitch, setShowNewSwitch] = useState(false);

  // Escenario: 'production' | 'design' | 'loc:<id>' (una sede concreta).
  const [scenario, setScenario] = useState(() => {
    try { return localStorage.getItem('cmdb.swd.scenario') || 'production'; } catch { return 'production'; }
  });
  // Filtro por ubicación: varias a la vez, incluyendo subgrupos.
  const [locationFilter, setLocationFilter] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('cmdb.swd.locations') || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch { return []; }
  });

  const [portLabelModal, setPortLabelModal] = useState(null); // { sw, port, value }
  const [switchConfig, setSwitchConfig] = useState(null);     // { sw, rowsText, installState }
  // Enlaces switch ↔ switch que pasan por panel y/o toma de pared: no son
  // latiguillos directos, pero el enlace existe y hay que verlo.
  const [mirrors, setMirrors] = useState([]);

  const canvasRef = useRef(null);
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const pageRef = useRef(null);

  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const panRef = useRef(pan);
  panRef.current = pan;
  const positionsRef = useRef(positions);
  positionsRef.current = positions;
  const hiddenRef = useRef(hiddenIds);
  hiddenRef.current = hiddenIds;
  const fittedRef = useRef(false);

  // ── Geometría y datos derivados ──────────────────────────────────────────
  const geometryById = useMemo(() => {
    const map = new Map();
    switches.forEach((sw) => map.set(sw.id, portGrid((sw.ports || []).length, sw.canvas?.rows, sw.canvas?.numbering)));
    return map;
  }, [switches]);

  const portInfoById = useMemo(() => {
    const map = new Map();
    switches.forEach((sw) => {
      const geo = geometryById.get(sw.id) || portGrid(0);
      (sw.ports || []).forEach((port, index) => {
        map.set(port.id, { sw, port, index, geo });
      });
    });
    return map;
  }, [switches, geometryById]);

  const hiddenSet = useMemo(() => new Set(hiddenIds), [hiddenIds]);

  // Switches visibles según el escenario elegido.
  // Sedes presentes en los switches, para el selector de escenario.
  const scenarioLocations = useMemo(() => {
    const map = new Map();
    switches.forEach((sw) => {
      if (sw.location_id && sw.location_name) map.set(sw.location_id, sw.location_name);
    });
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [switches]);

  // Lista completa (con jerarquía) para que el filtro entienda padres e hijos.
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    if (!companyId) return;
    api.getLocations('', companyId)
      .then((res) => {
        const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setLocations(rows);
      })
      .catch(() => {});
  }, [companyId]);

  const locationTree = useMemo(() => {
    if (locations.length) return locations;
    return scenarioLocations.map((loc) => ({ id: loc.id, name: loc.name, parent_id: null }));
  }, [locations, scenarioLocations]);

  const scenarioSwitches = useMemo(() => {
    const base = scenario === 'design'
      ? switches
      : switches.filter((sw) => (sw.install_state || 'verified') !== 'planned');
    if (!locationFilter.length) return base;
    return base.filter((sw) =>
      matchesLocation(locationTree, locationFilter, sw.location_id),
    );
  }, [switches, scenario, locationFilter, locationTree]);

  const scenarioSet = useMemo(
    () => new Set(scenarioSwitches.map((sw) => sw.id)),
    [scenarioSwitches]
  );

  const visibleSwitches = useMemo(
    () => scenarioSwitches.filter((sw) => !hiddenSet.has(sw.id)),
    [scenarioSwitches, hiddenSet]
  );

  // Latiguillos switch ↔ switch (una fila por PortConnection, sin duplicados)
  const edges = useMemo(() => {
    const out = [];
    const seen = new Set();
    switches.forEach((sw) => {
      (sw.ports || []).forEach((port) => {
        const conn = port.connection;
        if (!conn || seen.has(conn.id)) return;
        seen.add(conn.id);
        const aId = conn.port_a_type === 'switch_port' ? conn.port_a_id : conn.port_b_id;
        const bId = conn.port_a_type === 'switch_port' ? conn.port_b_id : conn.port_a_id;
        const a = portInfoById.get(aId);
        const b = portInfoById.get(bId);
        // Si un extremo no es un puerto de switch del lienzo, no se dibuja.
        if (!a || !b) return;
        out.push({
          id: conn.id, aId, bId, a, b, conn,
          cable_type: conn.cable_type,
          cable_color: conn.cable_color,
        });
      });
    });
    return out;
  }, [switches, portInfoById]);

  const visibleEdges = useMemo(
    () => edges.filter(
      (e) => scenarioSet.has(e.a.sw.id)
        && scenarioSet.has(e.b.sw.id)
        && !hiddenSet.has(e.a.sw.id)
        && !hiddenSet.has(e.b.sw.id)
    ),
    [edges, hiddenSet, scenarioSet]
  );

  // Enlaces «espejo»: switch — panel — (toma) — switch. Existen aunque no haya
  // latiguillo directo, así que se pintan como un cable más, discontinuo y con
  // la ristra de pasos (panel, toma…) en la etiqueta.
  const mirrorEdges = useMemo(() => {
    const out = [];
    const seen = new Set();
    (mirrors || []).forEach((link) => {
      const a = portInfoById.get(link.a_port_id);
      const b = portInfoById.get(link.b_port_id);
      if (!a || !b) return;
      if (!scenarioSet.has(a.sw.id) || !scenarioSet.has(b.sw.id)) return;
      if (hiddenSet.has(a.sw.id) || hiddenSet.has(b.sw.id)) return;
      const key = `${Math.min(link.a_port_id, link.b_port_id)}-${Math.max(link.a_port_id, link.b_port_id)}`;
      if (seen.has(key)) return;
      seen.add(key);
      const via = link.via || [];
      out.push({
        id: `mirror-${key}`,
        aId: link.a_port_id,
        bId: link.b_port_id,
        a,
        b,
        via,
        viaText: via.map((step) => step.label).filter(Boolean).join(' → '),
        derived: true,
      });
    });
    return out;
  }, [mirrors, portInfoById, hiddenSet, scenarioSet]);

  const totalConnections = useMemo(
    () => switches.reduce((acc, sw) => acc + (sw.ports || []).filter((p) => p.connection).length, 0),
    [switches]
  );
  // Puertos que van a equipos que NO son switches (paneles, APs, servidores…).
  // Cada latiguillo switch↔switch ocupa dos puertos de switch.
  const otherEquipmentPorts = Math.max(0, totalConnections - edges.length * 2);

  const filteredSwitches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scenarioSwitches;
    return scenarioSwitches.filter((sw) => {
      const hay = `${sw.name || ''} ${sw.code || ''} ${sw.model || ''} ${sw.ip_address || ''} ${sw.location_name || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [scenarioSwitches, search]);

  // ── Encuadre ─────────────────────────────────────────────────────────────
  const fitView = useCallback(() => {
    const box = canvasRef.current?.getBoundingClientRect?.();
    if (!box || box.width < 40) return;
    const visible = scenarioSwitches.filter((sw) => !hiddenRef.current.includes(sw.id));
    if (!visible.length) return;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    visible.forEach((sw) => {
      const p = positionsRef.current[sw.id];
      if (!p) return;
      const geo = portGrid((sw.ports || []).length, sw.canvas?.rows, sw.canvas?.numbering);
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x + geo.width);
      maxY = Math.max(maxY, p.y + geo.height);
    });
    if (!Number.isFinite(minX)) return;

    const pad = 44;
    const bw = Math.max(1, maxX - minX);
    const bh = Math.max(1, maxY - minY);
    const z = clamp(Math.min((box.width - pad * 2) / bw, (box.height - pad * 2) / bh), 0.35, 1.35);
    const usableW = box.width - pad * 2;
    const usableH = box.height - pad * 2;
    // Si el zoom mínimo (legibilidad) no deja encajar todo, alinea arriba-
    // izquierda en vez de centrar: centrar recortaría los dos extremos.
    const offX = bw * z <= usableW ? pad + (usableW - bw * z) / 2 : pad;
    const offY = bh * z <= usableH ? pad + (usableH - bh * z) / 2 : pad;
    setZoom(z);
    setPan({ x: offX - minX * z, y: offY - minY * z });
  }, [scenarioSwitches]);

  // Referencia estable para no encadenar `load` → `fitView` → `switches` y
  // provocar un bucle de recargas.
  const fitViewRef = useRef(null);
  fitViewRef.current = fitView;

  // ── Carga ────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      // Los switches en diseño (install_state='planned') también se cargan:
      // el escenario decide después cuáles se pintan.
      const res = await api.getSwitchCanvas(companyId, true);
      const list = res?.data?.switches || [];
      setSwitches(list);

      // Enlaces a través de panel/toma: si falla, el lienzo sigue funcionando.
      api.getCablingMirrors(companyId)
        .then((r) => setMirrors(r?.data?.links || []))
        .catch(() => setMirrors([]));

      const saved = {};
      const hidden = [];
      list.forEach((sw) => {
        const canvas = sw.canvas || {};
        if (typeof canvas.x === 'number' && typeof canvas.y === 'number') {
          saved[sw.id] = { x: canvas.x, y: canvas.y };
        } else if (positionsRef.current[sw.id]) {
          // Conserva lo que el usuario tenía en pantalla si aún no se ha guardado
          saved[sw.id] = positionsRef.current[sw.id];
        }
        if (canvas.hidden) hidden.push(sw.id);
      });

      const pending = list
        .filter((sw) => !saved[sw.id])
        .map((sw) => ({ id: sw.id, geo: portGrid((sw.ports || []).length, sw.canvas?.rows, sw.canvas?.numbering) }));

      if (pending.length) {
        const bottom = Object.values(saved).reduce((max, p) => Math.max(max, p.y), 0);
        const startY = Object.keys(saved).length ? bottom + VGAP + 120 : ORIGIN;
        Object.assign(saved, autoLayout(pending, startY));
      }

      setPositions(saved);
      positionsRef.current = saved;
      setHiddenIds(hidden);
      hiddenRef.current = hidden;

      if (!fittedRef.current && list.length) {
        fittedRef.current = true;
        setTimeout(() => fitViewRef.current?.(), 60);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // Al cambiar de empresa se recalcula todo (posiciones, encuadre…)
  useEffect(() => {
    fittedRef.current = false;
    setSelected(null);
    setGhost(null);
    setHoverPortId(null);
    setConnectModal(null);
    setConnModal(null);
    setSearch('');
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [companyId]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  // Escape = cancelar selección / cerrar modales
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (connectModal) { setConnectModal(null); return; }
      if (connModal) { setConnModal(null); return; }
      setSelected(null);
      setGhost(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [connectModal, connModal]);

  // Pantalla completa del lienzo (con `Esc` o el botón se sale).
  useEffect(() => {
    const onChange = () => setIsFull(document.fullscreenElement === pageRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
      return;
    }
    const el = pageRef.current;
    if (!el?.requestFullscreen) {
      setError('Este navegador no permite pantalla completa');
      return;
    }
    el.requestFullscreen().catch((e) => setError(e.message || 'No se pudo activar pantalla completa'));
  };

  // Etiquetas: puertos → completas → solo notas → ocultas
  const cycleLabels = () => {
    setLabelMode((mode) => {
      const next = mode === 'ports' ? 'full' : mode === 'full' ? 'notes' : mode === 'notes' ? 'off' : 'ports';
      try { localStorage.setItem('cmdb.swd.labels', next); } catch { /* sin almacenamiento */ }
      return next;
    });
  };

  const changeScenario = (value) => {
    setScenario(value);
    try { localStorage.setItem('cmdb.swd.scenario', value); } catch { /* sin almacenamiento */ }
    setSelected(null);
    setGhost(null);
    fittedRef.current = false;
    setTimeout(() => fitViewRef.current?.(), 80);
  };

  // Rueda = zoom sobre el cursor
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const current = zoomRef.current || 1;
      const next = clamp(current * (e.deltaY < 0 ? 1.12 : 1 / 1.12), ZMIN, ZMAX);
      const ratio = next / current;
      const p = panRef.current;
      setPan({
        x: mx - (mx - p.x) * ratio,
        y: my - (my - p.y) * ratio,
      });
      setZoom(next);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [companyId]);

  // ── Persistencia del layout ──────────────────────────────────────────────
  const persist = useCallback((nextPositions, nextHidden) => {
    if (!companyId) return;
    const pos = nextPositions || positionsRef.current;
    const hid = nextHidden || hiddenRef.current;
    api.saveSwitchCanvasLayout(pos, hid, companyId).catch((e) => setError(e.message));
  }, [companyId]);

  // ── Arrastrar tarjeta ────────────────────────────────────────────────────
  const startCardDrag = (e, sw) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const start = positionsRef.current[sw.id] || { x: 0, y: 0 };
    const z0 = zoomRef.current || 1;
    dragRef.current = { id: sw.id, ox: start.x, oy: start.y, mx: e.clientX, my: e.clientY, moved: false };

    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = (ev.clientX - d.mx) / z0;
      const dy = (ev.clientY - d.my) / z0;
      if (!d.moved && (Math.abs(dx) > 2 || Math.abs(dy) > 2)) d.moved = true;
      if (!d.moved) return;
      const next = {
        ...positionsRef.current,
        [d.id]: { x: Math.round(d.ox + dx), y: Math.round(d.oy + dy) },
      };
      positionsRef.current = next;
      setPositions(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const moved = dragRef.current?.moved;
      dragRef.current = null;
      if (moved) persist();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Arrastrar el lienzo (pan) ────────────────────────────────────────────
  const onCanvasDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest && e.target.closest('.swd-card')) return;
    const p = panRef.current;
    dragRef.current = { pan: true, mx: e.clientX, my: e.clientY, px: p.x, py: p.y };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d || !d.pan) return;
      setPan({ x: d.px + (ev.clientX - d.mx), y: d.py + (ev.clientY - d.my) });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      dragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const worldFromEvent = (e) => {
    const rect = svgRef.current?.getBoundingClientRect?.();
    if (!rect) return null;
    return {
      x: (e.clientX - rect.left - panRef.current.x) / (zoomRef.current || 1),
      y: (e.clientY - rect.top - panRef.current.y) / (zoomRef.current || 1),
    };
  };

  const onSvgMove = (e) => {
    if (!selected) return;
    const pt = worldFromEvent(e);
    if (!pt) return;
    setGhost((cur) => (
      cur && Math.abs(cur.x - pt.x) < 1.5 && Math.abs(cur.y - pt.y) < 1.5 ? cur : pt
    ));
  };

  // ── Visibilidad de switches ──────────────────────────────────────────────
  const toggleHidden = (id) => {
    const next = hiddenRef.current.includes(id)
      ? hiddenRef.current.filter((x) => x !== id)
      : [...hiddenRef.current, id];
    hiddenRef.current = next;
    setHiddenIds(next);
    persist(undefined, next);
  };

  const showAll = () => {
    hiddenRef.current = [];
    setHiddenIds([]);
    persist(undefined, []);
  };

  const focusSwitch = (sw) => {
    if (hiddenRef.current.includes(sw.id)) toggleHidden(sw.id);
    const p = positionsRef.current[sw.id];
    const box = canvasRef.current?.getBoundingClientRect?.();
    if (!p || !box) return;
    const geo = portGrid((sw.ports || []).length, sw.canvas?.rows, sw.canvas?.numbering);
    const z = Math.max(zoomRef.current || 1, 0.85);
    setZoom(z);
    setPan({
      x: box.width / 2 - (p.x + geo.width / 2) * z,
      y: box.height / 2 - (p.y + geo.height / 2) * z,
    });
  };

  const autoOrganize = () => {
    const ordered = scenarioSwitches.slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    const placed = autoLayout(
      ordered.map((sw) => ({ id: sw.id, geo: portGrid((sw.ports || []).length, sw.canvas?.rows, sw.canvas?.numbering) })),
      ORIGIN
    );
    // Solo se recolocan los del escenario actual; el resto conserva su sitio.
    const next = { ...positionsRef.current, ...placed };
    positionsRef.current = next;
    setPositions(next);
    persist(next);
    setNotice('Switches reorganizados en rejilla');
    setTimeout(() => fitView(), 60);
  };

  // ── Etiquetas de puerto (rotular puertos libres para diseñar) ────────────
  const portLabelsOf = (sw) => (
    sw?.canvas?.port_labels && typeof sw.canvas.port_labels === 'object' ? sw.canvas.port_labels : {}
  );

  const openPortLabel = (sw, port) => {
    setSelected(null);
    setGhost(null);
    setConnModal(null);
    setPortLabelModal({ sw, port, value: portLabelsOf(sw)[port.id] || '' });
  };

  const savePortLabel = async () => {
    if (!portLabelModal || saving) return;
    const { sw, port, value } = portLabelModal;
    const labels = { ...portLabelsOf(sw) };
    const text = value.trim();
    if (text) labels[port.id] = text;
    else delete labels[port.id];
    setSaving(true);
    setError(null);
    try {
      await api.saveSwitchCanvasLayout({ [sw.id]: { port_labels: labels } }, undefined, companyId);
      setPortLabelModal(null);
      setNotice(text
        ? `Etiqueta «${text}» en ${sw.name} ${portLabel(port)}`
        : 'Etiqueta de puerto borrada');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Configuración del switch: filas del panel y estado (diseño/producción)
  const openSwitchConfig = (sw) => {
    setSelected(null);
    setGhost(null);
    setSwitchConfig({
      sw,
      rowsText: Array.isArray(sw.canvas?.rows) ? sw.canvas.rows.join(', ') : '',
      numbering: sw.canvas?.numbering === 'alternate' ? 'alternate' : 'rows',
      installState: sw.install_state || 'verified',
    });
  };

  const saveSwitchConfig = async () => {
    if (!switchConfig || saving) return;
    const { sw, rowsText, numbering, installState } = switchConfig;
    const rows = rowsText
      .split(',')
      .map((part) => parseInt(part.trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0);
    setSaving(true);
    setError(null);
    try {
      await api.saveSwitchCanvasLayout(
        { [sw.id]: { rows: rows.length ? rows : null, numbering } },
        undefined,
        companyId
      );
      if ((sw.install_state || 'verified') !== installState) {
        await api.updateElement(sw.id, { install_state: installState }, companyId);
      }
      setSwitchConfig(null);
      setNotice(installState === 'verified'
        ? `${sw.name} pasó a producción`
        : `${sw.name} actualizado`);
      fittedRef.current = false;
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Puertos: selección y conexión ────────────────────────────────────────
  const describePort = (switchId, portId) => {
    const info = portInfoById.get(portId);
    return {
      switchId,
      portId,
      elementName: info?.sw?.name || '—',
      label: portLabel(info?.port),
    };
  };

  const openConnModal = (sw, port) => {
    const conn = port.connection;
    if (!conn) return;
    const other = conn.other_end || {};
    setSelected(null);
    setGhost(null);
    setForm({
      cable_type: conn.cable_type || 'Cat6',
      cable_color: conn.cable_color || 'azul',
      cable_length_m: conn.cable_length_m ?? '',
      notes: conn.notes || '',
    });
    setConnModal({
      conn,
      sw,
      port,
      aName: sw.name,
      aLabel: portLabel(port),
      otherName: other.element_name || '—',
      otherLabel: other.label || (other.port_number != null ? `P${other.port_number}` : '—'),
      otherIsSwitch: other.port_type === 'switch_port',
      otherSwitchId: other.port_type === 'switch_port' ? other.element_id : null,
    });
  };

  const handlePortClick = (e, sw, port) => {
    e.stopPropagation();
    if (e.detail > 1) return; // el doble clic abre la etiqueta del puerto
    setError(null);
    setNotice(null);

    // Ya hay un puerto seleccionado
    if (selected) {
      if (selected.portId === port.id) {
        setSelected(null);
        setGhost(null);
        return;
      }
      if (port.connection) {
        setNotice('Ese puerto ya tiene un cable: un puerto solo admite un latiguillo');
        return;
      }
      setConnectModal({
        a: describePort(selected.switchId, selected.portId),
        b: describePort(sw.id, port.id),
      });
      setSelected(null);
      setGhost(null);
      return;
    }

    // Sin selección
    if (port.connection) {
      openConnModal(sw, port);
      return;
    }
    setSelected({ switchId: sw.id, portId: port.id });
  };

  const doConnect = async () => {
    if (!connectModal) return;
    setSaving(true);
    setError(null);
    try {
      await api.createConnection({
        port_a_type: 'switch_port',
        port_a_id: connectModal.a.portId,
        port_b_type: 'switch_port',
        port_b_id: connectModal.b.portId,
        cable_type: form.cable_type || null,
        cable_color: form.cable_color || null,
        cable_length_m: form.cable_length_m === '' ? null : Number(form.cable_length_m),
        notes: form.notes || null,
      }, companyId);
      setConnectModal(null);
      setNotice(`Latiguillo creado: ${connectModal.a.elementName} ${connectModal.a.label} ↔ ${connectModal.b.elementName} ${connectModal.b.label}`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const doUpdateCable = async () => {
    if (!connModal) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateConnection(connModal.conn.id, {
        cable_type: form.cable_type || null,
        cable_color: form.cable_color || null,
        cable_length_m: form.cable_length_m === '' ? null : Number(form.cable_length_m),
        notes: form.notes || null,
      }, companyId);
      setConnModal(null);
      setNotice('Cable actualizado');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const doDisconnect = async () => {
    if (!connModal) return;
    setSaving(true);
    setError(null);
    try {
      await api.deleteConnection(connModal.conn.id, companyId);
      setConnModal(null);
      setNotice('Latiguillo desconectado');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Cables: geometría ────────────────────────────────────────────────────
  // El cable arranca en el BORDE de la tarjeta (arriba o abajo, según la fila
  // del puerto), no en el centro del puerto: así el trazado exterior puede ir
  // por debajo de las tarjetas y dentro solo queda un latiguillo corto desde
  // el puerto hasta el borde.

  // Carril de cada cable: los que no se solapan en X comparten carril (van
  // pegados a su tarjeta); los que sí se solapan se separan en carriles
  // distintos para no pisarse. Coloreado voraz por intervalos.
  const laneByEdge = useMemo(() => {
    const items = [];
    visibleEdges.forEach((edge) => {
      const pa = positions[edge.a.sw.id];
      const pb = positions[edge.b.sw.id];
      if (!pa || !pb) return;
      const ax = pa.x + portOffset(edge.a.geo, edge.a.index).x;
      const bx = pb.x + portOffset(edge.b.geo, edge.b.index).x;
      items.push({ id: edge.id, x1: Math.min(ax, bx), x2: Math.max(ax, bx) });
    });
    items.sort((a, b) => a.x1 - b.x1 || a.x2 - b.x2);
    const laneEnd = [];
    const map = new Map();
    items.forEach((it) => {
      let lane = laneEnd.findIndex((end) => end <= it.x1);
      if (lane === -1) { lane = laneEnd.length; laneEnd.push(it.x2); }
      else laneEnd[lane] = it.x2;
      map.set(it.id, lane);
    });
    return map;
  }, [visibleEdges, positions]);

  const edgePath = useCallback((edge) => {
    const pa = positions[edge.a.sw.id];
    const pb = positions[edge.b.sw.id];
    if (!pa || !pb) return null;
    const oa = portOffset(edge.a.geo, edge.a.index);
    const ob = portOffset(edge.b.geo, edge.b.index);
    const ax = pa.x + oa.x;
    const ay = pa.y + oa.y;
    const bx = pb.x + ob.x;
    const by = pb.y + ob.y;
    const sideA = exitSide(edge.a.geo, edge.a.index, ay, by);
    const sideB = exitSide(edge.b.geo, edge.b.index, by, ay);
    const ya = sideA === -1 ? pa.y : pa.y + edge.a.geo.height;
    const yb = sideB === -1 ? pb.y : pb.y + edge.b.geo.height;

    // Solo las dos tarjetas del propio cable (para poder bordearlas); nunca
    // ninguna otra, así mover un switch suelto no reenruta nada.
    const rectA = { x1: pa.x, y1: pa.y, x2: pa.x + edge.a.geo.width, y2: pa.y + edge.a.geo.height };
    const rectB = { x1: pb.x, y1: pb.y, x2: pb.x + edge.b.geo.width, y2: pb.y + edge.b.geo.height };
    const lane = (laneByEdge.get(edge.id) || 0) + (edge.derived ? 2 : 0);
    const pts = cableRoute(ax, ya, bx, yb, sideA, sideB, lane, rectA, rectB);
    return { x1: ax, y1: ay, x2: bx, y2: by, mid: polylineMid(pts), d: roundedPath(pts) };
  }, [positions, laneByEdge]);

  // Latiguillo interior: del puerto al borde de su tarjeta, con el color del
  // cable. Se pinta por encima de la tarjeta para que se vea dónde entra.
  // Latiguillo interior: del puerto al borde de su tarjeta, con el color del
  // cable. Se pinta por encima de la tarjeta para que se vea dónde entra.
  // Un mismo puerto puede llevar DOS (su cable real y el enlace por panel/toma),
  // así que se guarda una lista por puerto: si solo se guardaba uno, el espejo
  // se quedaba clavado en el borde de la tarjeta sin llegar al conector.
  const leads = useMemo(() => {
    const map = new Map();
    const push = (portId, lead) => {
      const list = map.get(portId) || [];
      if (!list.some((l) => l.side === lead.side && l.color === lead.color)) list.push(lead);
      map.set(portId, list);
    };
    const add = (edge, derived) => {
      const pa = positions[edge.a.sw.id];
      const pb = positions[edge.b.sw.id];
      if (!pa || !pb) return;
      const oa = portOffset(edge.a.geo, edge.a.index);
      const ob = portOffset(edge.b.geo, edge.b.index);
      const ay = pa.y + oa.y;
      const by = pb.y + ob.y;
      const color = derived ? 'violeta' : edge.cable_color;
      push(edge.a.port.id, {
        x: oa.x, y: oa.y, color,
        side: exitSide(edge.a.geo, edge.a.index, ay, by),
      });
      push(edge.b.port.id, {
        x: ob.x, y: ob.y, color,
        side: exitSide(edge.b.geo, edge.b.index, by, ay),
      });
    };
    visibleEdges.forEach((edge) => add(edge, false));
    mirrorEdges.forEach((edge) => add(edge, true));
    return map;
  }, [visibleEdges, mirrorEdges, positions]);

  const ghostLine = useMemo(() => {
    if (!selected || !ghost) return null;
    const info = portInfoById.get(selected.portId);
    const p = info ? positionsRef.current[info.sw.id] : null;
    if (!info || !p) return null;
    const o = portOffset(info.geo, info.index);
    const x1 = p.x + o.x;
    const y1 = p.y + o.y;
    const side = exitSide(info.geo, info.index, y1, ghost.y);
    const yEdge = side === -1 ? p.y : p.y + info.geo.height;
    const reach = clamp(Math.hypot(ghost.x - x1, ghost.y - y1) * 0.3, 26, 130);
    return {
      x1, y1, x2: ghost.x, y2: ghost.y,
      px: x1, py: y1, side, yEdge,
      d: `M ${x1} ${yEdge} C ${x1} ${yEdge + side * reach}, ${ghost.x} ${ghost.y - side * reach * 0.35}, ${ghost.x} ${ghost.y}`,
    };
  }, [selected, ghost, portInfoById]);

  const hoverPeerId = useMemo(() => {
    if (!hoverPortId) return null;
    const info = portInfoById.get(hoverPortId);
    const other = info?.port?.connection?.other_end;
    return other?.port_type === 'switch_port' ? other.port_id : null;
  }, [hoverPortId, portInfoById]);

  // ── Estilos de puerto ────────────────────────────────────────────────────
  const portClass = (port, isSelected, isPeer, isBlaze) => {
    const classes = ['swd-port'];
    if (port.is_active === false) classes.push('is-off');
    else if (port.connection) {
      classes.push(port.connection.other_end?.port_type === 'switch_port' ? 'is-switch' : 'is-other');
    } else if (port.is_connected) classes.push('is-legacy');
    else classes.push('is-free');
    if (port.poe_enabled) classes.push('is-poe');
    if (isSelected) classes.push('is-selected');
    if (isPeer) classes.push('is-peer');
    if (isBlaze) classes.push('is-blaze');
    return classes.join(' ');
  };

  const selectedPortId = selected?.portId || null;
  const hiddenCount = hiddenIds.length;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div ref={pageRef} className={`swd-page${embedded ? ' is-tabbed' : ''}${isFull ? ' is-fullscreen' : ''}`}>
      <div className="swd-toolbar">
        <span className="swd-title">
          <ArrowsRightLeftIcon className="cbd-ico is-title" aria-hidden="true" />
          Diseñador de Switches
        </span>
        <span className="swd-stats">
          {visibleSwitches.length}/{scenarioSwitches.length} switches · {visibleEdges.length} latiguillos
          {mirrorEdges.length > 0 ? ` · ${mirrorEdges.length} por panel/toma` : ''}
          {otherEquipmentPorts > 0 ? ` · ${otherEquipmentPorts} puertos a otros equipos` : ''}
        </span>
        <span className="swd-spacer" />
        <label className="swd-scenario" title="Producción solo muestra switches validados; Diseño incluye los planificados">
          Escenario
          <select value={scenario} onChange={(e) => changeScenario(e.target.value)}>
            <option value="production">Producción</option>
            <option value="design">Diseño (todos)</option>
          </select>
        </label>
        <LocationFilter
          companyId={companyId}
          value={locationFilter}
          extraItems={scenarioLocations}
          onChange={(ids) => {
            setLocationFilter(ids);
            try { localStorage.setItem('cmdb.swd.locations', JSON.stringify(ids)); } catch { /* noop */ }
          }}
        />
        {selected && (
          <span className="swd-sel-hint">
            ✦ Puerto seleccionado — clic en otro puerto libre
          </span>
        )}
        <button type="button" className="swd-btn is-primary" onClick={() => setShowNewSwitch(true)} title="Crear un switch sin salir del diseñador">
          ＋ Nuevo switch
        </button>
        <button
          type="button"
          className={`swd-btn${labelMode === 'off' ? '' : ' is-on'}`}
          onClick={cycleLabels}
          title="Etiquetas de los cables: puertos → completas → solo notas → ocultas"
        >
          🏷 {LABEL_MODE_TEXT[labelMode] || 'Etiquetas'}
        </button>
        <button type="button" className="swd-btn" onClick={autoOrganize} title="Colocar todos los switches en rejilla">
          ⌗ Auto-organizar
        </button>
        <button type="button" className="swd-btn" onClick={fitView} title="Encajar la vista en la pantalla">
          ⊞ Ajustar
        </button>
        <button
          type="button"
          className="swd-btn"
          onClick={toggleFullscreen}
          title={isFull ? 'Salir de pantalla completa (Esc)' : 'Pantalla completa'}
        >
          {isFull ? '🗗 Salir' : '⛶ Pantalla completa'}
        </button>
        <button type="button" className="swd-btn" onClick={load} disabled={loading} title="Recargar switches y cables">
          {loading ? '…' : '↻ Refrescar'}
        </button>
      </div>

      {error && (
        <div className="swd-banner is-error">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} title="Cerrar">×</button>
        </div>
      )}
      {notice && <div className="swd-banner is-notice">{notice}</div>}

      <div className="swd-body">
        <aside className="swd-side">
          <input
            className="swd-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar switch (nombre, IP, ubicación)…"
          />
          <div className="swd-side-head">
            <span>Switches</span>
            {hiddenCount > 0 && (
              <button type="button" className="swd-link" onClick={showAll}>
                Mostrar {hiddenCount} ocultos
              </button>
            )}
          </div>
          <div className="swd-side-list">
            {filteredSwitches.map((sw) => {
              const conn = (sw.ports || []).filter((p) => p.connection).length;
              const isHidden = hiddenSet.has(sw.id);
              return (
                <div key={sw.id} className={`swd-side-row${isHidden ? ' is-hidden' : ''}`}>
                  <button
                    type="button"
                    className="swd-eye"
                    title={isHidden ? 'Mostrar en el lienzo' : 'Ocultar del lienzo'}
                    onClick={() => toggleHidden(sw.id)}
                  >
                    {isHidden ? '🚫' : '👁'}
                  </button>
                  <button type="button" className="swd-side-info" onClick={() => focusSwitch(sw)} title="Centrar en el lienzo">
                    <span className="swd-side-name">{sw.name}</span>
                    <span className="swd-side-meta">
                      {(sw.ports || []).length} puertos · {conn} conectados
                      {sw.location_name ? ` · ${sw.location_name}` : ''}
                      {sw.install_state === 'planned' ? ' · diseño' : ''}
                    </span>
                  </button>
                </div>
              );
            })}
            {!filteredSwitches.length && (
              <div className="swd-empty">
                {loading ? 'Cargando switches…' : 'Sin switches que coincidan'}
              </div>
            )}
          </div>

          <div className="swd-legend">
            <div className="swd-legend-row"><i className="swd-dot is-free" /> Puerto libre</div>
            <div className="swd-legend-row"><i className="swd-dot is-switch" /> Cableado a switch</div>
            <div className="swd-legend-row"><i className="swd-dot is-mirror" /> Enlace por panel/toma</div>
            <div className="swd-legend-row"><i className="swd-dot is-other" /> Cableado a otro equipo</div>
            <div className="swd-legend-row"><i className="swd-dot is-off" /> Inactivo</div>
            <div className="swd-legend-row"><i className="swd-dot is-poe" /> Entrega PoE</div>
            <div className="swd-legend-tip">
              Arrastra un switch por su tarjeta para moverlo. Rueda = zoom.
              <br />
              Doble clic en un puerto = etiquetarlo. Doble clic en el nombre = filas y estado.
            </div>
          </div>
        </aside>

        <div className="swd-canvas-wrap" ref={canvasRef}>
          <svg
            ref={svgRef}
            className="swd-svg"
            onMouseDown={onCanvasDown}
            onMouseMove={onSvgMove}
            onMouseLeave={() => setGhost(null)}
          >
            <defs>
              <pattern id="swd-grid" width="26" height="26" patternUnits="userSpaceOnUse">
                <circle cx="1.2" cy="1.2" r="1.1" fill="var(--swd-grid-dot)" />
              </pattern>
              {/* Halo del cable resaltado (se ve en su propio color) */}
              <filter id="swd-hot" x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur stdDeviation="5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <rect className="swd-bg" x="0" y="0" width="100%" height="100%" fill="url(#swd-grid)" />

            <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
              {/* Zonas de clic de los cables: van DEBAJO de las tarjetas para no
                  robar los clics de los puertos. */}
              {visibleEdges.map((edge) => {
                const geo = edgePath(edge);
                if (!geo) return null;
                const title = `${edge.a.sw.name} ${edge.a.port.label || `P${edge.a.port.port_number}`}`
                  + ` ↔ ${edge.b.sw.name} ${edge.b.port.label || `P${edge.b.port.port_number}`}`
                  + `${edge.cable_type ? ` · ${edge.cable_type}` : ''}`
                  + `${edge.cable_color ? ` · ${edge.cable_color}` : ''}`;
                return (
                  <path
                    key={`hit-${edge.id}`}
                    className="swd-edge-hit"
                    d={geo.d}
                    onClick={() => openConnModal(edge.a.sw, edge.a.port)}
                  >
                    <title>{title}</title>
                  </path>
                );
              })}

              {/* Cables, también DEBAJO de las tarjetas: el tramo exterior no
                  tapa nombres ni puertos. El latiguillo interior (puerto →
                  borde) se pinta con los puertos, más abajo. */}
              {visibleEdges.map((edge) => {
                const geo = edgePath(edge);
                if (!geo) return null;
                const color = colorHex(edge.cable_color);
                const hovered = hoverPortId === edge.aId || hoverPortId === edge.bId;
                return (
                  <g key={`line-${edge.id}`} className={`swd-edge${hovered ? ' is-hover' : ''}`}>
                    <path
                      className="swd-edge-line"
                      d={geo.d}
                      stroke={color}
                      strokeDasharray={isFiber(edge.cable_type) ? '7 4' : undefined}
                    />
                  </g>
                );
              })}

              {/* Enlaces por panel y/o toma («espejo» de puerto): discontinuos
                  y en violeta, para no confundirlos con un latiguillo directo. */}
              {mirrorEdges.map((edge) => {
                const geo = edgePath(edge);
                if (!geo) return null;
                const hovered = hoverPortId === edge.aId || hoverPortId === edge.bId;
                return (
                  <g key={`line-${edge.id}`} className={`swd-edge is-mirror${hovered ? ' is-hover' : ''}`}>
                    <path className="swd-edge-line" d={geo.d} stroke="var(--cbd-backbone, #8b5cf6)" strokeDasharray="7 5">
                      <title>
                        {`${edge.a.sw.name} ${portLabel(edge.a.port)} ⇄ ${edge.b.sw.name} ${portLabel(edge.b.port)}`
                          + `\nPasa por: ${edge.viaText || '—'}`}
                      </title>
                    </path>
                  </g>
                );
              })}

              {/* Etiquetas de los cables: encima de la línea, debajo de las
                  tarjetas para que no tapen su contenido. */}
              {labelMode !== 'off' && visibleEdges.map((edge) => {
                const geo = edgePath(edge);
                if (!geo) return null;
                const lines = cableLabelLines(edge, labelMode);
                if (!lines.length) return null;
                const w = Math.max(...lines.map((l) => l.length)) * 4.6 + 12;
                const h = lines.length * 10 + 7;
                const y0 = 3 - (lines.length - 1) * 5;
                return (
                  <g
                    key={`label-${edge.id}`}
                    className="swd-cable-label"
                    transform={`translate(${geo.mid.x},${geo.mid.y})`}
                  >
                    <title>{lines.join(' · ')}</title>
                    <rect
                      x={-w / 2}
                      y={-h / 2}
                      width={w}
                      height={h}
                      rx={4}
                      stroke={colorHex(edge.cable_color)}
                    />
                    {lines.map((line, i) => (
                      <text key={i} x={0} y={y0 + i * 10} textAnchor="middle">
                        {line}
                      </text>
                    ))}
                  </g>
                );
              })}

              {/* Etiquetas de los enlaces por panel/toma */}
              {labelMode !== 'off' && mirrorEdges.map((edge) => {
                const geo = edgePath(edge);
                if (!geo) return null;
                const lines = mirrorLabelLines(edge, labelMode);
                if (!lines.length) return null;
                const w = Math.max(...lines.map((l) => l.length)) * 4.6 + 12;
                const h = lines.length * 10 + 7;
                const y0 = 3 - (lines.length - 1) * 5;
                return (
                  <g
                    key={`label-${edge.id}`}
                    className="swd-cable-label is-mirror"
                    transform={`translate(${geo.mid.x},${geo.mid.y})`}
                  >
                    <title>{`${edge.a.sw.name} ${portLabel(edge.a.port)} ⇄ ${edge.b.sw.name} ${portLabel(edge.b.port)} · ${edge.viaText}`}</title>
                    <rect x={-w / 2} y={-h / 2} width={w} height={h} rx={4} stroke="#8b5cf6" />
                    {lines.map((line, i) => (
                      <text key={i} x={0} y={y0 + i * 10} textAnchor="middle">
                        {line}
                      </text>
                    ))}
                  </g>
                );
              })}

              {/* Tarjetas de switch */}
              {visibleSwitches.map((sw) => {
                const geo = geometryById.get(sw.id) || portGrid(0);
                const pos = positions[sw.id];
                if (!pos) return null;
                const ports = sw.ports || [];
                const connected = ports.filter((p) => p.connection).length;
                const subtitle = [
                  sw.model || (sw.port_count ? `${sw.port_count} puertos` : 'Sin puertos'),
                  `${connected}/${ports.length || 0} conectados`,
                  sw.location_name,
                ].filter(Boolean).join(' · ');

                return (
                  <g
                    key={sw.id}
                    className={`swd-card${hiddenSet.has(sw.id) ? ' is-hidden' : ''}${sw.install_state === 'planned' ? ' is-design' : ''}`}
                    transform={`translate(${pos.x},${pos.y})`}
                  >
                    <title>{`${sw.name}${sw.ip_address ? ` · ${sw.ip_address}` : ''}\n${subtitle}`}</title>

                    {/* Cuerpo de la tarjeta: arrastrar aquí */}
                    <rect
                      className="swd-card-rect"
                      width={geo.width}
                      height={geo.height}
                      rx={9}
                      onMouseDown={(e) => startCardDrag(e, sw)}
                    />
                    {/* Columna de información a la izquierda: deja libre toda la
                        franja superior e inferior de los puertos, que es por
                        donde entran y salen los latiguillos. Doble clic aquí
                        para configurar filas y estado (diseño/producción). */}
                    <path
                      className="swd-card-info"
                      d={`M ${geo.infoW} 0 L 9 0 Q 0 0 0 9 L 0 ${geo.height - 9} Q 0 ${geo.height} 9 ${geo.height} L ${geo.infoW} ${geo.height} Z`}
                      onMouseDown={(e) => startCardDrag(e, sw)}
                      onDoubleClick={(e) => { e.stopPropagation(); openSwitchConfig(sw); }}
                    >
                      <title>Doble clic: configuración del switch (filas y estado)</title>
                    </path>
                    <g
                      className="swd-card-head-text"
                      onMouseDown={(e) => startCardDrag(e, sw)}
                      onDoubleClick={(e) => { e.stopPropagation(); openSwitchConfig(sw); }}
                    >
                      <text x={12} y={geo.height / 2 - 10} className="swd-card-name">
                        {truncate(sw.name, 16)}
                      </text>
                      <text x={12} y={geo.height / 2 + 4} className="swd-card-sub">
                        {truncate(sw.model || (sw.port_count ? `${sw.port_count} puertos` : 'Sin puertos'), 22)}
                      </text>
                      <text x={12} y={geo.height / 2 + 16} className="swd-card-sub">
                        {truncate(`${connected}/${ports.length || 0} conectados${sw.install_state === 'planned' ? ' · DISEÑO' : ''}`, 24)}
                      </text>
                    </g>

                    {!ports.length && (
                      <text x={(geo.infoW + geo.width) / 2} y={geo.height / 2 + 3} className="swd-card-empty" textAnchor="middle">
                        Sin puertos configurados
                      </text>
                    )}

                    {/* Panel de puertos: se pinta en una capa aparte, POR ENCIMA
                        de los cables, para que el cable quede "enchufado" al
                        puerto y no lo tape. Ver capa de puertos más abajo. */}
                  </g>
                );
              })}

              {/* Capa de PUERTOS (encima de las tarjetas y de los cables). */}
              {visibleSwitches.map((sw) => {
                const geo = geometryById.get(sw.id) || portGrid(0);
                const pos = positions[sw.id];
                if (!pos) return null;
                return (
                  <g key={`ports-${sw.id}`} transform={`translate(${pos.x},${pos.y})`}>

                    {/* Latiguillo interior: del borde de la tarjeta al puerto. */}
                    {(sw.ports || []).map((port) => (
                      (leads.get(port.id) || []).map((lead, i) => {
                        const yPort = lead.side === -1 ? lead.y - PORT_H / 2 : lead.y + PORT_H / 2;
                        const yEdge = lead.side === -1 ? 0 : geo.height;
                        const hovered = hoverPortId === port.id || hoverPeerId === port.id;
                        return (
                          <line
                            key={`lead-${port.id}-${i}`}
                            className={`swd-lead${hovered ? ' is-hover' : ''}`}
                            x1={lead.x} y1={yPort}
                            x2={lead.x} y2={yEdge}
                            stroke={colorHex(lead.color)}
                          />
                        );
                      })
                    ))}

                    {(sw.ports || []).map((port, index) => {
                      const off = portOffset(geo, index);
                      const hasLead = (leads.get(port.id) || []).length > 0;
                      const isSel = selectedPortId === port.id;
                      const isPeer = hoverPeerId === port.id;
                      const other = port.connection?.other_end;
                      const tip = [
                        `${port.label || `Puerto ${port.port_number}`}`,
                        port.vlan ? `VLAN ${port.vlan}` : null,
                        port.speed ? port.speed : null,
                        port.poe_enabled ? 'PoE' : null,
                        port.connection
                          ? `→ ${other?.element_name || '?'} ${other?.label || (other?.port_number != null ? `P${other.port_number}` : '')}`
                          : (port.is_connected ? 'marcado como conectado (sin latiguillo)' : 'libre'),
                      ].filter(Boolean).join(' · ');

                      // El número va dentro del conector, así el latiguillo no lo
                      // tapa salga el cable por arriba o por abajo.
                      const leadLocal = ghostLine && isSel && !hasLead ? ghostLine : null;
                      const portLabelText = portLabelsOf(sw)[port.id] || '';
                      const tipFull = [tip, portLabelText ? `Etiqueta: ${portLabelText}` : null, 'Doble clic: etiquetar puerto']
                        .filter(Boolean).join(' · ');

                      return (
                        <g
                          key={port.id}
                          className={portClass(port, isSel, isPeer, hoverPortId === port.id || isPeer || (hoverPeerId === port.id))}
                          transform={`translate(${off.x - PORT_W / 2},${off.y - PORT_H / 2})`}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => handlePortClick(e, sw, port)}
                          onDoubleClick={(e) => { e.stopPropagation(); openPortLabel(sw, port); }}
                          onMouseEnter={() => setHoverPortId(port.id)}
                          onMouseLeave={() => setHoverPortId((cur) => (cur === port.id ? null : cur))}
                        >
                          <rect width={PORT_W} height={PORT_H} rx={2.5} />
                          {port.poe_enabled && <circle className="swd-port-poe" cx={PORT_W - 2.5} cy={2.5} r={2.2} />}
                          {leadLocal && (
                            <line
                              className="swd-lead is-ghost"
                              x1={PORT_W / 2} x2={PORT_W / 2}
                              y1={leadLocal.side === -1 ? 0 : PORT_H}
                              y2={leadLocal.side === -1
                                ? -(off.y - PORT_H / 2)
                                : geo.height - (off.y - PORT_H / 2)}
                            />
                          )}
                          <text x={PORT_W / 2} y={PORT_H / 2 + 2.5} textAnchor="middle" className="swd-port-num">
                            {port.port_number}
                          </text>
                          {labelMode !== 'off' && portLabelText && (() => {
                            // La etiqueta va en la banda libre; si hay cable, en la
                            // contraria a su salida. Sin cable, se escalona para que
                            // dos puertos contiguos no se pisen.
                            let above;
                            const firstLead = (leads.get(port.id) || [])[0];
                            if (firstLead?.side === 1) above = true;
                            else if (firstLead?.side === -1) above = false;
                            else above = index % 2 === 0;
                            const y = above ? -5 : PORT_H + 12;
                            return (
                              <g className="swd-port-label">
                                <rect x={PORT_W / 2 - 13} y={y - 7.5} width={26} height={9.5} rx={2} />
                                <text x={PORT_W / 2} y={y} textAnchor="middle">
                                  {truncate(portLabelText, 7)}
                                </text>
                              </g>
                            );
                          })()}
                          <title>{tipFull}</title>
                        </g>
                      );
                    })}
                  </g>
                );
              })}

              {/* Latiguillo en curso */}
              {ghostLine && (
                <path
                  className="swd-edge-ghost"
                  d={ghostLine.d}
                  stroke="var(--swd-accent)"
                />
              )}
            </g>
          </svg>

          {!switches.length && !loading && (
            <div className="swd-canvas-empty">
              <div className="swd-canvas-empty-ic">🔀</div>
              No hay switches en esta empresa.
              <div className="swd-canvas-empty-sub">
                Crea switches en Infraestructura → Switches y vuelve aquí para cablearlos.
              </div>
            </div>
          )}

          <div className="swd-zoom">
            <button type="button" onClick={() => setZoom((z) => clamp(z * 1.2, ZMIN, ZMAX))} title="Acercar">＋</button>
            <button type="button" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} title="Zoom 100 %">
              {Math.round(zoom * 100)}%
            </button>
            <button type="button" onClick={() => setZoom((z) => clamp(z / 1.2, ZMIN, ZMAX))} title="Alejar">－</button>
          </div>
        </div>
      </div>

      {/* ── Modal: crear latiguillo ── */}
      {connectModal && (
        <div className="swd-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConnectModal(null); }}>
          <div className="swd-modal">
            <h3 className="swd-modal-title">🔗 Nuevo latiguillo entre switches</h3>

            <div className="swd-endpoints">
              <div className="swd-endpoint">
                <span className="swd-endpoint-tag">Puerto A</span>
                <strong>{connectModal.a.elementName}</strong>
                <span className="swd-endpoint-port">{connectModal.a.label}</span>
              </div>
              <div className="swd-endpoint-arrow">↔</div>
              <div className="swd-endpoint">
                <span className="swd-endpoint-tag">Puerto B</span>
                <strong>{connectModal.b.elementName}</strong>
                <span className="swd-endpoint-port">{connectModal.b.label}</span>
              </div>
            </div>

            <div className="swd-form-grid">
              <label>
                Tipo de cable
                <select value={form.cable_type} onChange={(e) => setForm((f) => ({ ...f, cable_type: e.target.value }))}>
                  {CABLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label>
                Color
                <select value={form.cable_color} onChange={(e) => setForm((f) => ({ ...f, cable_color: e.target.value }))}>
                  {CABLE_COLORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </label>
              <label>
                Longitud (m)
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.cable_length_m}
                  onChange={(e) => setForm((f) => ({ ...f, cable_length_m: e.target.value }))}
                  placeholder="opcional"
                />
              </label>
              <label>
                Etiqueta / notas
                <input
                  type="text"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Ej: Trunk VLAN 13, Host SRP-PGEN00…"
                />
              </label>
            </div>

            <p className="swd-modal-hint">
              Se guarda como cable físico entre los dos puertos: aparecerá también en la
              pestaña <em>Puertos</em> de cada switch.
            </p>

            <div className="swd-modal-actions">
              <button type="button" className="swd-btn is-ghost" onClick={() => setConnectModal(null)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="swd-btn is-primary" onClick={doConnect} disabled={saving}>
                {saving ? 'Guardando…' : '🔗 Conectar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: cable existente ── */}
      {connModal && (
        <div className="swd-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConnModal(null); }}>
          <div className="swd-modal">
            <h3 className="swd-modal-title">🔌 Latiguillo existente</h3>

            <div className="swd-endpoints">
              <div className="swd-endpoint">
                <span className="swd-endpoint-tag">Puerto A</span>
                <strong>{connModal.aName}</strong>
                <span className="swd-endpoint-port">{connModal.aLabel}</span>
              </div>
              <div className="swd-endpoint-arrow">↔</div>
              <div className="swd-endpoint">
                <span className="swd-endpoint-tag">{connModal.otherIsSwitch ? 'Puerto B' : 'Equipo destino'}</span>
                <strong>{connModal.otherName}</strong>
                <span className="swd-endpoint-port">{connModal.otherLabel}</span>
              </div>
            </div>

            <div className="swd-form-grid">
              <label>
                Tipo de cable
                <select value={form.cable_type} onChange={(e) => setForm((f) => ({ ...f, cable_type: e.target.value }))}>
                  {CABLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label>
                Color
                <select value={form.cable_color} onChange={(e) => setForm((f) => ({ ...f, cable_color: e.target.value }))}>
                  {CABLE_COLORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </label>
              <label>
                Longitud (m)
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.cable_length_m}
                  onChange={(e) => setForm((f) => ({ ...f, cable_length_m: e.target.value }))}
                />
              </label>
              <label>
                Etiqueta / notas
                <input
                  type="text"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Ej: Trunk VLAN 13, Host SRP-PGEN00…"
                />
              </label>
            </div>

            {!connModal.otherIsSwitch && (
              <p className="swd-modal-hint">
                El otro extremo no es un switch, así que el cable no se dibuja en este lienzo
                (se ve en la ficha del switch y en el Canvas de Red).
              </p>
            )}

            <div className="swd-modal-actions">
              {connModal.otherIsSwitch && connModal.otherSwitchId && (
                <button
                  type="button"
                  className="swd-btn is-ghost"
                  onClick={() => {
                    const target = switches.find((s) => s.id === connModal.otherSwitchId);
                    setConnModal(null);
                    if (target) focusSwitch(target);
                  }}
                >
                  Ir al otro switch ↗
                </button>
              )}
              <span className="swd-spacer" />
              <button
                type="button"
                className="swd-btn is-ghost"
                onClick={() => openPortLabel(connModal.sw, connModal.port)}
                disabled={saving}
                title="Rotular este puerto en el lienzo"
              >
                🏷 Etiqueta del puerto
              </button>
              <button type="button" className="swd-btn is-danger" onClick={doDisconnect} disabled={saving}>
                ✂ Desconectar
              </button>
              <button type="button" className="swd-btn is-primary" onClick={doUpdateCable} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar cable'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: etiqueta de puerto ── */}
      {portLabelModal && (
        <div className="swd-overlay" onClick={(e) => { if (e.target === e.currentTarget) setPortLabelModal(null); }}>
          <div className="swd-modal">
            <h3 className="swd-modal-title">🏷 Etiqueta del puerto</h3>
            <p className="swd-modal-hint" style={{ marginTop: 0 }}>
              {portLabelModal.sw.name} · {portLabel(portLabelModal.port)}
              {portLabelModal.port.connection ? ' · ya cableado' : ' · libre'}
            </p>
            <div className="swd-form-grid" style={{ gridTemplateColumns: '1fr' }}>
              <label>
                Texto de la etiqueta
                <input
                  type="text"
                  autoFocus
                  maxLength={60}
                  value={portLabelModal.value}
                  onChange={(e) => setPortLabelModal((m) => ({ ...m, value: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') savePortLabel(); }}
                  placeholder="Ej: A UTP-12, Reservado, Trunk al core…"
                />
              </label>
            </div>
            <p className="swd-modal-hint">
              Se pinta junto al puerto en el lienzo. Sirve para rotular puertos libres
              mientras diseñas el cableado.
            </p>
            <div className="swd-modal-actions">
              <button type="button" className="swd-btn is-ghost" onClick={() => setPortLabelModal(null)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="swd-btn is-primary" onClick={savePortLabel} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar etiqueta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: configuración del switch (filas y estado) ── */}
      {switchConfig && (
        <div className="swd-overlay" onClick={(e) => { if (e.target === e.currentTarget) setSwitchConfig(null); }}>
          <div className="swd-modal">
            <h3 className="swd-modal-title">⚙️ {switchConfig.sw.name}</h3>
            <div className="swd-form-grid">
              <label>
                Puertos por fila
                <input
                  type="text"
                  value={switchConfig.rowsText}
                  onChange={(e) => setSwitchConfig((c) => ({ ...c, rowsText: e.target.value }))}
                  placeholder="auto"
                />
                <span className="swd-field-hint">
                  Ej: <code>12, 12</code> → 12 arriba y el resto abajo. Vacío = automático.
                </span>
              </label>
              <label>
                Numeración
                <select
                  value={switchConfig.numbering}
                  onChange={(e) => setSwitchConfig((c) => ({ ...c, numbering: e.target.value }))}
                >
                  <option value="rows">Por filas (1-12 · 13-24)</option>
                  <option value="alternate">Intercalada (1, 3, 5… · 2, 4, 6…)</option>
                </select>
                <span className="swd-field-hint">
                  Intercalada = impares en la fila de arriba y pares en la de abajo.
                </span>
              </label>
              <label style={{ gridColumn: '1 / -1' }}>
                Estado
                <select
                  value={switchConfig.installState}
                  onChange={(e) => setSwitchConfig((c) => ({ ...c, installState: e.target.value }))}
                >
                  <option value="planned">{INSTALL_LABEL.planned}</option>
                  <option value="mounted">{INSTALL_LABEL.mounted}</option>
                  <option value="verified">{INSTALL_LABEL.verified}</option>
                </select>
                <span className="swd-field-hint">
                  «Validado» lo deja en producción; «Planificado» solo en escenarios de diseño.
                </span>
              </label>
            </div>
            <div className="swd-modal-actions">
              <button type="button" className="swd-btn is-ghost" onClick={() => setSwitchConfig(null)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="swd-btn is-primary" onClick={saveSwitchConfig} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alta de switch sin salir del diseñador. El formulario es el mismo que
          usa Infraestructura → Switches, así que el registro queda idéntico. */}
      {showNewSwitch && (
        <PortElementForm
          elementType="switch"
          item={null}
          companyId={companyId}
          initialMode="create"
          onClose={() => setShowNewSwitch(false)}
          onSave={async () => {
            setShowNewSwitch(false);
            setNotice('Switch creado: ya está en el lienzo');
            await load();
          }}
        />
      )}
    </div>
  );
}

export default SwitchWiringCanvas;
